import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';
import { map } from 'rxjs/operators';

import { LayoutService } from '@app/core/layout.service';
import { ActivationFlowService } from '@app/shared/tenant-lifecycle/activation/activation-flow.service';
import { TenantLifecycleBannerComponent } from '@app/shared/tenant-lifecycle/banner/tenant-lifecycle-banner.component';

import { ActionsComponent } from './actions/actions.component';
import { NavigationComponent } from './navigation/navigation.component';
import { UserInfoComponent } from './user-info/user-info.component';

const nonClosableSidebarMediaQuery = '(min-width: 960px)';

@Component({
  selector: 'hypecast-admin-base-layout',
  templateUrl: './base-layout.component.html',
  styleUrl: './base-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouterOutlet,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    UserInfoComponent,
    NavigationComponent,
    ActionsComponent,
    TenantLifecycleBannerComponent,
  ],
})
export class BaseLayoutComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly layoutService = inject(LayoutService);
  private readonly sidenavContainer = viewChild(MatSidenavContainer);

  readonly isHandset = toSignal(
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  readonly isSidebarNonClosable = toSignal(
    this.breakpointObserver.observe(nonClosableSidebarMediaQuery).pipe(map(({ matches }) => matches)),
    { initialValue: false },
  );

  readonly vh100ContentSection = this.layoutService.vh100ContentSection;
  readonly isSidebarCollapsed = signal(false);

  constructor() {
    // ?activate=1 opens the activation modal on any admin route (links from mails or the studio).
    inject(ActivationFlowService).watchDeepLinks(inject(DestroyRef));
  }

  toggleSidebarCollapsed(): void {
    this.isSidebarCollapsed.update((collapsed) => !collapsed);
    this.sidenavContainer()?.updateContentMargins();
  }

  onSidebarTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName === 'width') {
      this.sidenavContainer()?.updateContentMargins();
    }
  }
}
